import { DocumentRecord, documentFromJSON } from "common/records/Document";
import { RootActions, RootState } from "common/reducers";
import {
  DocumentActions,
  EDIT_DOCUMENT,
  EDIT_DOCUMENTS,
  FETCH_DOCUMENT,
  FETCH_DOCUMENTS,
  RECEIVE_DOCUMENT,
  RECEIVE_DOCUMENTS,
  SET_DOCUMENTS_LOADED,
  SET_DOCUMENT_LOADED,
  REMOVE_DOCUMENT,
  RESET_DOCUMENT,
  TOGGLE_DOCUMENT_DISPLAY,
  TOGGLE_ALL_DOCUMENTS_DISPLAY,
  PRODUCT_DOCUMENT,
  ESTIMATE_LINE_ITEM_DOCUMENT,
  EDIT_DISPLAY_BOOLEAN,
  UPDATE_RESOURCE_DOCUMENT_DISPLAY,
} from "common/reducers/document.actions";
import { List, Map, Record } from "immutable";
import { fetch, loaded, receive, reset } from "common/reducers/Utils";
import { reducer as idsManagementReducer } from "common/reducers/idsManagement.reducer";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { reducer as formReducer } from "common/reducers/rsfForm.reducer";
import { RsfFormActions } from "common/reducers/rsfForm.actions";
import { setDisplay } from "common/records/Document";
import { currentJobId } from "common/selectors/job.selectors";

export const DocumentStateRecord = Record({
  byId: Map<number, DocumentRecord>(),
  lastSavedById: Map<number, DocumentRecord>(),
  unsavedId: -1,
  loadingMeasurements: false,
});

export const initialState = DocumentStateRecord();
export type DocumentState = typeof initialState;
export const model = "documents";

export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }

  switch (action.type) {
    case FETCH_DOCUMENT:
      return fetch(state, model, documentFromJSON({ id: action.payload.id })) as RootState;

    case RECEIVE_DOCUMENT: {
      return receive(state, model, documentFromJSON(action.payload.document)) as RootState;
    }

    case SET_DOCUMENT_LOADED:
      return loaded(state, model, action.payload.id) as RootState;

    case EDIT_DOCUMENT:
      return state.setIn([model, "byId", action.payload.id, action.payload.name], action.payload.value);

    case RESET_DOCUMENT:
      return reset(state, model, action.payload.id) as RootState;

    case EDIT_DISPLAY_BOOLEAN: {
      const { rootPath, name } = action.payload.event;

      state = formReducer(state, RsfFormActions.update(action.payload.event));
      state = reducer(state, DocumentActions.toggleDocumentDisplay(rootPath, name));

      let record = state.getIn(rootPath) as DocumentRecord;

      // Edge case. Technically, this shouldn't happen.
      if (
        name === "displayInProposalWatermark" ||
        name === "displayInAgreementWatermark" ||
        name === "displayInResource"
      ) {
        record = record.set("displayInAgreement", false);
        record = record.set("displayInProposal", false);
        state = state.setIn(rootPath, record);
      }
      return state.setIn(rootPath.concat(["display"]), setDisplay(record));
    }

    case TOGGLE_DOCUMENT_DISPLAY:
      const { rootPath, displayProp } = action.payload;
      // Turn off all other display props
      switch (displayProp) {
        case "displayInResource":
          return state
            .setIn(rootPath.concat(["displayInAgreementWatermark"]), false)
            .setIn(rootPath.concat(["displayInProposalWatermark"]), false);
        case "displayInAgreementWatermark":
          return state
            .setIn(rootPath.concat(["displayInResource"]), false)
            .setIn(rootPath.concat(["displayInProposalWatermark"]), false);
        case "displayInProposalWatermark":
          return state
            .setIn(rootPath.concat(["displayInAgreementWatermark"]), false)
            .setIn(rootPath.concat(["displayInResource"]), false);
        default:
          return state;
      }

    case FETCH_DOCUMENTS:
      action.payload.ids.forEach((id) => {
        state = reducer(state, DocumentActions.fetchDocument(id));
      });

      return state;

    case RECEIVE_DOCUMENTS:
      action.payload.documents.forEach((d) => {
        state = reducer(state, DocumentActions.receiveDocument(d));
      });

      return state;

    case SET_DOCUMENTS_LOADED:
      action.payload.ids.forEach((id) => {
        state = reducer(state, DocumentActions.setDocumentLoaded(id));
      });

      return state;

    case REMOVE_DOCUMENT:
      state = idsManagementReducer(state, IdsManagementActions.removeId(action.payload.id, model));

      return state.deleteIn([model, "byId", action.payload.id]);

    case EDIT_DOCUMENTS:
      action.payload.ids.forEach((id) => {
        state = reducer(state, DocumentActions.editDocument(id, action.payload.name, action.payload.value));
      });

      return state;

    case TOGGLE_ALL_DOCUMENTS_DISPLAY:
      action.payload.ids.forEach((id) => {
        state = reducer(
          state,
          DocumentActions.editDisplayBoolean({
            rootPath: [model, "byId", id],
            name: action.payload.displayProp,
            value: action.payload.value,
          }),
        );
      });

      return state;

    case UPDATE_RESOURCE_DOCUMENT_DISPLAY:
      const jobResourceDocuments = state.getIn([
        "jobs",
        "byId",
        currentJobId(state),
        "config",
        "resource_documents",
      ]) as List<any>;
      const jobResourceDocument = jobResourceDocuments?.find(
        (rd: DocumentRecord) => rd.get("id") === action.payload.id,
      );
      if (jobResourceDocument) {
        state = state
          .setIn(
            [model, "byId", action.payload.id, "displayInAgreement"],
            jobResourceDocument.get("displayInAgreement"),
          )
          .setIn([model, "byId", action.payload.id, "displayInProposal"], jobResourceDocument.get("displayInProposal"));
      }

      return state;

    // PRODUCTS
    case PRODUCT_DOCUMENT:
      let docIds = state.getIn(["products", "byId", action.payload.productId, "document_ids"]) as List<number>;
      let unsavedId = state.getIn([model, "unsavedId"]);
      const document = action.payload.document;
      document.id = unsavedId;

      const documentRecord = documentFromJSON(document);

      docIds = docIds.unshift(unsavedId);
      state = state.setIn([model, "byId", unsavedId], documentRecord);
      unsavedId -= 1;

      return state
        .setIn(["products", "byId", action.payload.productId, "document_ids"], docIds)
        .setIn([model, "unsavedId"], unsavedId);

    // ESTIMATE LINE ITEM
    case ESTIMATE_LINE_ITEM_DOCUMENT: {
      const lineItemId = action.payload.estimateLineItemId;
      const lineItemDocIds = (state.getIn(["estimateLineItems", "byId", lineItemId, "document_ids"]) ??
        List<number>()) as List<number>;
      let nextUnsavedId = state.getIn([model, "unsavedId"]) as number;
      const docPayload = { ...action.payload.document, id: nextUnsavedId };
      const newDocumentRecord = documentFromJSON(docPayload);

      const updatedDocIds = lineItemDocIds.push(nextUnsavedId);
      state = state.setIn([model, "byId", nextUnsavedId], newDocumentRecord);
      nextUnsavedId -= 1;

      return state
        .setIn(["estimateLineItems", "byId", lineItemId, "document_ids"], updatedDocIds)
        .setIn([model, "unsavedId"], nextUnsavedId);
    }

    default:
      return state;
  }
};
